import { create } from 'zustand';
import { getBridge } from '@/platform';
import { hasLaunchSignature } from '@/platform/shared';
import { MiniappError, isBackendConfigured, messageOf } from '@/api/miniapp/client';
import { miniappApi } from '@/api/miniapp/miniappApi';
import { registerSessionGuard } from '@/api/miniapp/sessionGuard';
import type { AuthResponse, AuthUser, RegistrationStep } from '@/api/miniapp/types';

/**
 * Состояние сессии мини-приложения по контракту FRONTEND_API.md.
 *
 * Отделено от доменных сторов: здесь жизненный цикл входа и регистрации,
 * а не данные курса. Основной интерфейс открывается только в фазе
 * `authenticated`; всё остальное ведёт по сценарию регистрации.
 */

export type AuthPhase =
  /** Адрес бэкенда не задан: входить некуда. */
  | 'disabled'
  | 'idle'
  | 'authenticating'
  | 'authenticated'
  | 'registration'
  | 'error';

interface AuthState {
  phase: AuthPhase;
  /** Шаг сценария регистрации, когда phase = registration. */
  step: RegistrationStep | null;
  user: AuthUser | null;
  error: string | null;
  /** Пользователь вошёл с другого устройства — отдельное сообщение. */
  replacedByOtherDevice: boolean;
  /** Вход уже удавался: повторная попытка идёт молча, не разбирая экран. */
  everAuthenticated: boolean;
  lastCheckedAt: number | null;
  /** Момент удачного входа: по нему видно, что сессия теряется сразу после него. */
  authOkAt: number | null;
  /** Сколько раз сессия пропадала сразу после входа. */
  dropsAfterAuth: number;
  /**
   * Клиент не сохраняет cookie сессии.
   *
   * Так ведёт себя десктопное приложение: мини-приложение живёт во фрейме,
   * то есть в стороннем контексте, куда cookie с `SameSite=Lax` не
   * отправляется. Вход проходит, но следующий же запрос уходит без сессии.
   * Молча повторять вход бесполезно — нужно сказать об этом прямо.
   */
  cookieBlocked: boolean;

  authenticate(): Promise<AuthPhase>;
  /** Одна итерация опроса. false — сессия потеряна. */
  ping(): Promise<boolean>;
  /** Применить ответ ручки регистрации (auth/registration/complete). */
  applyResponse(response: AuthResponse): AuthPhase;
  /** Осознанный повторный вход после сообщения о входе с другого устройства. */
  continueHere(): Promise<AuthPhase>;
  /** Ещё одна попытка входа после сообщения о неработающей cookie. */
  retryAfterCookieBlock(): Promise<AuthPhase>;
  /** Условия приняты: ручка согласия возвращает только флаг, не состояние. */
  markConsentGiven(): void;
  reset(): void;
}

/**
 * Ответ бэкенда → фаза и шаг стора.
 *
 * Шаг берётся только из ответа. `auth` и `session` при регистрации возвращают
 * `state` без `step` (см. раздел 1 контракта), поэтому подставлять здесь
 * значение по умолчанию нельзя: опрос сессии раз в 25 секунд откатывал бы
 * пользователя на ввод почты с любого следующего шага. Известный шаг
 * сохраняется, и только первый вход, когда шага ещё нет, начинается с почты.
 */
function resolvePhase(
  response: AuthResponse,
  currentStep: RegistrationStep | null
): { phase: AuthPhase; step: RegistrationStep | null } {
  if (response.state === 'authenticated') return { phase: 'authenticated', step: null };
  return { phase: 'registration', step: response.step ?? currentStep ?? 'email_code' };
}

/**
 * Определение шага регистрации, когда бэкенд его не прислал.
 *
 * `auth` и `session` возвращают только `state`, поэтому после перезапуска
 * приложения неизвестно, на чём пользователь остановился. Признак берётся из
 * документированного поведения: список профилей доступен лишь после
 * подтверждения почты, а `REGISTRATION_REQUIRED` означает, что почта ещё нет.
 *
 * Это обход пробела в контракте. Когда `auth` начнёт присылать `step`,
 * функцию нужно убрать.
 */
async function resolveStepFromServer(): Promise<void> {
  try {
    await miniappApi.getProfiles();
    useAuthStore.setState({ step: 'registration_profile' });
  } catch (error) {
    const needsEmail = error instanceof MiniappError && error.type === 'REGISTRATION_REQUIRED';
    useAuthStore.setState({ step: needsEmail ? 'email_code' : useAuthStore.getState().step });
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  phase: isBackendConfigured ? 'idle' : 'disabled',
  step: null,
  user: null,
  error: null,
  replacedByOtherDevice: false,
  everAuthenticated: false,
  lastCheckedAt: null,
  authOkAt: null,
  dropsAfterAuth: 0,
  cookieBlocked: false,

  applyResponse(response) {
    const { phase, step } = resolvePhase(response, get().step);
    set({
      phase,
      step,
      everAuthenticated: get().everAuthenticated || phase === 'authenticated',
      // Часть ручек возвращает только состояние: затирать пользователя нельзя,
      // иначе теряются почта и признак согласия, по которым строится поток
      user: response.user ?? get().user,
      error: null,
      replacedByOtherDevice: false,
    });
    return phase;
  },

  markConsentGiven() {
    const user = get().user;
    set({ user: { ...(user ?? {}), consent: true } });
  },

  async authenticate() {
    if (!isBackendConfigured) return 'disabled';
    if (get().phase === 'authenticating') return get().phase;

    // Шаг до входа: он подсказывает, известен ли уже пройденный этап
    const hadStep = get().step !== null;

    set({ phase: 'authenticating', error: null });
    try {
      const bridge = getBridge();
      const platform = bridge.id === 'max' ? 'max' : 'telegram';
      const initData = bridge.getInitData();

      // Без подписи запрос заведомо не пройдёт, а ответ сервера про неё
      // («InitDate не содержит hash») человеку ничего не объясняет. Причина
      // почти всегда одна: приложение открыто не кнопкой мессенджера
      if (!hasLaunchSignature(initData)) {
        set({
          phase: 'error',
          error:
            'Приложение открыто без данных запуска. Закройте его и откройте заново кнопкой в чате с ботом.',
        });
        return 'error';
      }

      const response = await miniappApi.auth(platform, initData);
      const phase = get().applyResponse(response);
      set({ lastCheckedAt: Date.now(), authOkAt: Date.now() });

      // Холодный старт посреди регистрации: шага в ответе нет, а начинать с
      // почты нельзя — она уже подтверждена, и повторный код запросить некуда
      if (phase === 'registration' && !response.step && !hadStep) await resolveStepFromServer();

      return phase;
    } catch (error) {
      const message =
        messageOf(error, 'Не удалось подключиться к серверу');
      set({ phase: 'error', error: message });
      return 'error';
    }
  },

  async ping() {
    if (!isBackendConfigured) return true;

    try {
      const response = await miniappApi.session();
      get().applyResponse(response);
      // Продление дошло — значит cookie возвращается серверу и подозрение
      // на её блокировку снимается
      set({ lastCheckedAt: Date.now(), dropsAfterAuth: 0, cookieBlocked: false });
      return true;
    } catch (error) {
      // Любая ошибка продления означает потерю сессии: опрос прекращается,
      // выполняется повторный вход. Отдельно отмечаем вход с другого устройства.
      const replaced = error instanceof MiniappError && error.type === 'MINIAPP_SESSION_REPLACED';
      if (!replaced) noteSessionDrop();
      set({ phase: 'idle', error: null, replacedByOtherDevice: replaced });
      return false;
    }
  },

  async continueHere() {
    set({ replacedByOtherDevice: false });
    return get().authenticate();
  },

  async retryAfterCookieBlock() {
    set({ cookieBlocked: false, dropsAfterAuth: 0 });
    return get().authenticate();
  },

  reset() {
    set({
      phase: isBackendConfigured ? 'idle' : 'disabled',
      step: null,
      user: null,
      error: null,
      replacedByOtherDevice: false,
      everAuthenticated: false,
      lastCheckedAt: null,
      authOkAt: null,
      dropsAfterAuth: 0,
      cookieBlocked: false,
    });
  },
}));

/** Сессия считается потерянной «сразу после входа» в пределах этого окна. */
const FRESH_SESSION_MS = 60000;

/** Двух срывов подряд достаточно: одиночный бывает и от разрыва связи. */
const COOKIE_BLOCK_THRESHOLD = 2;

/**
 * Учёт сессии, пропавшей сразу после удачного входа.
 *
 * Одиночный случай — обычная накладка, но повторение означает, что клиент не
 * возвращает cookie: вход проходит, а следующий запрос уходит без сессии.
 */
function noteSessionDrop(): void {
  const { authOkAt, dropsAfterAuth } = useAuthStore.getState();
  if (authOkAt === null || Date.now() - authOkAt > FRESH_SESSION_MS) return;

  const drops = dropsAfterAuth + 1;
  useAuthStore.setState({ dropsAfterAuth: drops, cookieBlocked: drops >= COOKIE_BLOCK_THRESHOLD });
}

/**
 * Контрактные ошибки сессии на любых ручках приводят к тем же переходам, что и
 * на продлении. Обработчики регистрируются один раз при загрузке модуля: так
 * ни один стор не может забыть про них при добавлении нового запроса.
 */
registerSessionGuard({
  onSessionLost(replaced) {
    const { phase } = useAuthStore.getState();
    // Повторные 401 из параллельных запросов не должны перебивать уже
    // показанное сообщение о входе с другого устройства
    if (!replaced) noteSessionDrop();
    if (phase === 'idle') return;
    useAuthStore.setState({ phase: 'idle', error: null, replacedByOtherDevice: replaced });
  },

  onRegistrationRequired() {
    const { phase, step, everAuthenticated } = useAuthStore.getState();
    // Пользователь уже проходил вход, а сервер снова требует регистрацию —
    // значит запрос ушёл без сессии
    if (everAuthenticated) noteSessionDrop();
    if (phase === 'registration') return;
    // Шаг может быть неизвестен: без него поток регистрации показал бы пустой
    // экран, поэтому начинаем с подтверждения почты — первого шага сценария
    useAuthStore.setState({
      phase: 'registration',
      step: step ?? 'email_confirmation',
      error: null,
    });
  },

  onRegistrationCompleted() {
    // Состояние на клиенте устарело: актуальный экран определяет сервер
    void useAuthStore.getState().ping();
  },
});
